import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { GameItem } from "store/gameCenter/games/slice";
import { apiRoutes } from "configs/apiRoutes";
import instance from "api/apiClient";

import SearchInput from "components/inputs/SearchInput/SearchInput";
import Pagination from "components/Pagination";

import ImageWithFallback from "helpers/ImageWithFallback";
import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";

const gamesPerPageOptions = [15, 50, 100, 250, 500, 1000];
const defaultGamesPerPage = 50;

const TournamentsGamesTab: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isGamesLoading, setIsGamesLoading] = useState(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [debouncedSearchValue, setDebouncedSearchValue] = useState<string>("");
  const [activePage, setActivePage] = useState<number>(1);
  const [totalItems, setTotalItems] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [gamesList, setGamesList] = useState<GameItem[]>([]);
  const [currentLimit, setLimit] = useState<number>(defaultGamesPerPage);

  const handleLoadGames = async () => {
    setIsGamesLoading(true);

    try {
      const result = await instance.get(
        `${apiRoutes.tournamentGamesList}/${searchParams.get("entityId")}?limit=${currentLimit}&offset=${activePage - 1}&gameTitle=${debouncedSearchValue}`,
      );

      setGamesList(result.data.items);
      setTotalItems(result.data.totalItems);
      setTotalPages(result.data.totalPages);
    } catch {
      console.log("handleLoadGames error");
    } finally {
      setIsGamesLoading(false);
    }
  };

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      setActivePage(1);
      setDebouncedSearchValue(searchValue.trim());
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [searchValue]);

  useEffect(() => {
    handleLoadGames();
  }, [debouncedSearchValue, activePage, currentLimit]);

  const onSearch = (e: string) => {
    setSearchValue(e);
  };

  const handlePrevClick = () => {
    if (activePage > 1) {
      setActivePage(activePage - 1);
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      setActivePage(activePage + 1);
    }
  };

  return (
    <div className="wager-list-games tournament-games">
      <div className="games-order-search">
        <SearchInput
          placeholder={t("searchTournament")}
          value={searchValue}
          onChange={onSearch}
          isSmall
        />
      </div>
      <div className="games-order-list tournament-games-list">
        <div className="item">
          <div className="order">ID</div>
          <div className="content">{t("title")}</div>
          <div className="provider">{t("provider")}</div>
        </div>

        {isGamesLoading ? (
          <ModalTabSkeleton />
        ) : gamesList.length === 0 ? (
          <div className="tournament-games-empty">
            {t("noTournamentGamesFound")}
          </div>
        ) : (
          gamesList.map((el) => (
            <div className="item" key={el.id}>
              <div className="order">{el.id}</div>
              <div className="content">
                <ImageWithFallback
                  src={el.image as string}
                  alt=""
                  className="game-img"
                />
                {el.title}
              </div>
              <div className="provider">{el.provider.title}</div>
            </div>
          ))
        )}
      </div>
      <Pagination
        handleLimitClick={(e) => {
          setLimit(e);
          setActivePage(1);
        }}
        activePage={activePage}
        limit={currentLimit}
        limitsArray={gamesPerPageOptions}
        totalItems={totalItems}
        totalPages={totalPages}
        handleNextClick={handleNextClick}
        handlePrevClick={handlePrevClick}
      />
    </div>
  );
};

export default TournamentsGamesTab;
